import {
  Arrange,
  Dropdown,
  Icon,
  IconButton,
  Link,
  Split,
  SplitSection,
  SvgCheckCircleFill,
  SvgChevronSmallDown,
  SvgInfo,
  Text,
  TextButton,
  Tooltip,
} from '@loomhq/lens';
import classNames from 'classnames';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { showConfirmationModal } from 'actions/confirmationModal';
import {
  requestVideoPlayerPause,
  updateFillerWordHighlight,
  updateIsFillerRemovalFTUX,
  updateTrimmingFillerWords,
} from 'actions/videoPlayer';
import {
  DEFAULT_FILLER_REMOVAL_CLICK,
  FILLER_REMOVAL_DROPDOWN_CLOSE_OUT,
} from 'constants/events';
import {
  FillerWordRemovalOption,
  NONE,
  TEXT,
  TEXT_AUDIO,
  TRANSCRIPTION_STATUS_TRIMMING,
  IN_PROGRESS as TRANSCRIPT_IN_PROGRESS,
} from 'constants/videoTranscript';
import { updateTranscriptByFillerWordRemoval } from 'creators/shareVideo';
import { patchUserVideoSettings } from 'creators/user';
import {
  selectFillerWordRanges,
  selectFillerWordRemoval,
  selectIsFillerRemovalFTUX,
  selectIsTrimmingFillerWords,
  selectNumFillerWordsInVideoVersion,
  selectTranscriptionStatus,
} from 'selectors/transcripts';
import { selectDefaultFillerWordRemoval } from 'selectors/user';
import { selectCurrentUserIsOwner } from 'selectors/videoPlayer';
import * as analytics from 'utilities/analytics';

import styles from './styles.module.less';

const FILLER_REMOVAL_HELP_ARTICLE =
  'https://support.loom.com/hc/en-us/articles/360019796378';

const getOptionTitle = (selection: FillerWordRemovalOption): string => {
  switch (selection) {
    case TEXT: {
      return 'Text only';
    }
    case TEXT_AUDIO:
      return 'Text and audio';
    default:
      return 'Do not remove';
  }
};

interface OptionTitleProps {
  option: FillerWordRemovalOption;
  selectedFillerRemovalOption: FillerWordRemovalOption;
  description: string;
  link?: string;
  disabled: boolean;
  defaultFillerWordRemoval: FillerWordRemovalOption;
}

const OptionTitle = ({
  option,
  selectedFillerRemovalOption,
  description,
  link,
  disabled,
  defaultFillerWordRemoval,
}: OptionTitleProps): JSX.Element => {
  return (
    <div className={styles.fillerWordRemovalItemBody}>
      <Split justifyContent="space-between" alignItems="center">
        <SplitSection>
          <Text fontWeight="medium">
            {getOptionTitle(option)}{' '}
            {option === TEXT_AUDIO && (
              <Text fontWeight="medium" isInline={true}>
                beta
              </Text>
            )}
            {defaultFillerWordRemoval === option && (
              <>
                {' '}
                <Text fontWeight="medium" isInline={true}>
                  (default)
                </Text>
              </>
            )}
          </Text>
        </SplitSection>
        <SplitSection>
          {selectedFillerRemovalOption === option && (
            <Icon
              color={disabled ? 'disabledContent' : 'body'}
              icon={<SvgCheckCircleFill />}
              size={3}
            />
          )}
        </SplitSection>
      </Split>
      <Text color="bodyDimmed" size="small">
        {description}
      </Text>
      {link && (
        <Text size="small">
          <Link
            target="_blank"
            variant="neutral"
            href={FILLER_REMOVAL_HELP_ARTICLE}
            onClick={event => event.stopPropagation()}
          >
            {link}
          </Link>
        </Text>
      )}
    </div>
  );
};

const FillerWordRemoval = ({
  showFullHeader,
  iconSize,
  dropdownOpen,
  setDropdownOpen,
}: {
  showFullHeader: boolean;
  iconSize: 'small' | 'medium';
  dropdownOpen: boolean;
  setDropdownOpen: (v: boolean) => void;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const fillerWordRanges = useSelector<unknown, unknown[]>(
    selectFillerWordRanges
  );
  const fillerWordRemoval = useSelector<unknown, FillerWordRemovalOption>(
    selectFillerWordRemoval
  );
  const numFillerWordsInVideo = useSelector<number>(
    selectNumFillerWordsInVideoVersion
  );
  const showFillerWordRemovalHeader = useSelector(selectCurrentUserIsOwner);
  const transcriptStatus = useSelector<unknown, string>(
    selectTranscriptionStatus
  );
  const transcriptIsInProgress = [
    TRANSCRIPT_IN_PROGRESS,
    TRANSCRIPTION_STATUS_TRIMMING,
  ].includes(transcriptStatus);
  const isFillerRemovalFTUX = useSelector(selectIsFillerRemovalFTUX);
  const fillerWordsPresent = Number(numFillerWordsInVideo) > 0;
  const isTrimmingFillerWords = useSelector<unknown, boolean>(
    selectIsTrimmingFillerWords
  );
  const defaultFillerWordRemoval =
    useSelector<unknown, FillerWordRemovalOption | null>(
      selectDefaultFillerWordRemoval
    ) || NONE;
  const [selectedFillerRemoval, setSelectedFillerRemoval] =
    useState<FillerWordRemovalOption>(fillerWordRemoval);
  const [isFillerWordsDismissed, setIsFillerWordsDismissed] = useState(false);
  const [isDefaultOptionSelected, setIsDefaultOptionSelected] = useState(
    defaultFillerWordRemoval === selectedFillerRemoval
  );

  useEffect(() => {
    setDropdownOpen(false);
  }, [fillerWordRemoval, setDropdownOpen]);

  useEffect(() => {
    setIsDefaultOptionSelected(
      defaultFillerWordRemoval === selectedFillerRemoval
    );
  }, [selectedFillerRemoval, defaultFillerWordRemoval]);

  useEffect(() => {
    if (isFillerRemovalFTUX) {
      return;
    }

    const shouldShowFillerWordHighlight =
      dropdownOpen && fillerWordRemoval === NONE;

    dispatch(updateFillerWordHighlight(shouldShowFillerWordHighlight));

    return () => {
      dispatch(updateFillerWordHighlight(false));
    };
  }, [dropdownOpen, dispatch, fillerWordRemoval, isFillerRemovalFTUX]);

  const updateFillerWords = fillerWordRemoval => {
    if (fillerWordRemoval === NONE) {
      setDropdownOpen(false);
    }

    if (fillerWordRemoval === selectedFillerRemoval) {
      analytics.track(FILLER_REMOVAL_DROPDOWN_CLOSE_OUT, {
        result: 'No new filler removal option selected',
        transcriptIsInProgress,
      });

      return;
    }

    dispatch(updateTrimmingFillerWords(true));
    dispatch(updateTranscriptByFillerWordRemoval(fillerWordRemoval));
    setSelectedFillerRemoval(fillerWordRemoval);
    dispatch(requestVideoPlayerPause());
  };

  const onDefaultSettingButtonClick = () => {
    dispatch(
      patchUserVideoSettings({
        default_filler_word_removal: selectedFillerRemoval,
      })
    );

    analytics.track(DEFAULT_FILLER_REMOVAL_CLICK, {
      defaultFillerWordRemoval: selectedFillerRemoval,
    });
  };

  const disabledDropdown = transcriptIsInProgress || isTrimmingFillerWords;

  const textOption = {
    key: TEXT,
    title: (
      <OptionTitle
        option={TEXT}
        selectedFillerRemovalOption={selectedFillerRemoval}
        description="Remove &lsquo;um&rsquo; and &lsquo;uh&rsquo; from the transcript and closed captions."
        disabled={disabledDropdown}
        defaultFillerWordRemoval={defaultFillerWordRemoval}
      />
    ),
    onClick: () => {
      updateFillerWords(TEXT);
      dispatch(showConfirmationModal('Filler words removed from text!'));
    },
    disabled: disabledDropdown,
  };

  const textAudioOption = {
    key: TEXT_AUDIO,
    title: (
      <OptionTitle
        option={TEXT_AUDIO}
        selectedFillerRemovalOption={selectedFillerRemoval}
        description="Remove &lsquo;um&rsquo; and &lsquo;uh&rsquo; from the transcript and closed captions, and mute them in your video&rsquo;s audio."
        link="Viewers may still hear audio."
        disabled={disabledDropdown}
        defaultFillerWordRemoval={defaultFillerWordRemoval}
      />
    ),
    onClick: () => {
      updateFillerWords(TEXT_AUDIO);
      dispatch(
        showConfirmationModal('Filler words removed from text and audio!')
      );
    },
    disabled: disabledDropdown,
  };

  const noneOption = {
    key: NONE,
    title: (
      <OptionTitle
        option={NONE}
        selectedFillerRemovalOption={selectedFillerRemoval}
        description="Keep all instances of &lsquo;um&rsquo; and &lsquo;uh&rsquo; in the text and audio."
        disabled={disabledDropdown}
        defaultFillerWordRemoval={defaultFillerWordRemoval}
      />
    ),
    onClick: (_: React.MouseEvent) => {
      updateFillerWords(NONE);
      dispatch(showConfirmationModal('Filler words are no longer removed'));
    },
    disabled: disabledDropdown,
  };

  const dropdownCloseOut = useCallback(
    shouldDropdownBeOpen => {
      setDropdownOpen(shouldDropdownBeOpen);

      if (!shouldDropdownBeOpen) {
        analytics.track(FILLER_REMOVAL_DROPDOWN_CLOSE_OUT, {
          result: 'No new filler removal option selected',
          transcriptIsInProgress,
        });
      }
    },
    [setDropdownOpen, transcriptIsInProgress]
  );

  if (transcriptIsInProgress) {
    return (
      <div
        className={classNames([
          styles.fillerWordsNotice,
          styles.fillerWordsNotPresent,
        ])}
      >
        <Arrange alignItems="center" justifyContent="space-between">
          <Text size="small" fontWeight="medium">
            Searching for filler words...
          </Text>

          <div>
            <Dropdown
              menuMaxWidth={32}
              menuPosition="right"
              trigger={
                <div className={styles.dropdownTrigger}>
                  <TextButton
                    size={iconSize}
                    iconPosition="right"
                    icon={<SvgChevronSmallDown />}
                    onClick={() => {
                      dropdownCloseOut(!dropdownOpen);
                    }}
                  >
                    {selectedFillerRemoval === NONE
                      ? 'Remove'
                      : getOptionTitle(selectedFillerRemoval)}
                  </TextButton>
                </div>
              }
              options={[textOption, textAudioOption, noneOption]}
              onOuterClick={() => dropdownCloseOut(false)}
            />
          </div>
        </Arrange>
      </div>
    );
  }

  if (isFillerWordsDismissed || !showFillerWordRemovalHeader) {
    return null;
  }

  const infoButtonProps = {
    as: 'a',
    href: FILLER_REMOVAL_HELP_ARTICLE,
    target: '_blank',
  };

  return (
    <div
      className={classNames([
        styles.fillerWordsNotice,
        {
          [styles.scrolled]: !showFullHeader && !dropdownOpen,
          [styles.highlightFillerWordsHeader]: isFillerRemovalFTUX,
          [styles.fillerWordsPresent]: fillerWordsPresent,
          [styles.fillerWordsNotPresent]: !fillerWordsPresent,
        },
      ])}
      onMouseEnter={() =>
        selectedFillerRemoval === NONE &&
        dispatch(updateFillerWordHighlight(true))
      }
      onMouseLeave={() =>
        selectedFillerRemoval === NONE &&
        dispatch(updateFillerWordHighlight(false))
      }
    >
      <Arrange gap="small" autoFlow="row" justifyContent="stretch">
        <Arrange alignItems="center" justifyContent="space-between">
          <Arrange alignItems="center" justifyContent="space-between">
            <Text size="small" fontWeight="medium">
              {fillerWordRanges.length} filler{' '}
              {fillerWordRanges.length === 1 ? 'word' : 'words'}{' '}
              {fillerWordRemoval === NONE ? 'detected' : 'removed'}
            </Text>

            <Tooltip content="Learn more">
              <div className={styles.fillerIconButton}>
                <IconButton
                  altText="Info"
                  icon={<SvgInfo />}
                  size={iconSize}
                  color="bodyDimmed"
                  {...infoButtonProps}
                />
              </div>
            </Tooltip>
          </Arrange>

          {fillerWordsPresent && (
            <div>
              <Dropdown
                menuMaxWidth={32}
                menuPosition="right"
                trigger={
                  <div className={styles.dropdownTrigger}>
                    <TextButton
                      size={iconSize}
                      iconPosition="right"
                      icon={<SvgChevronSmallDown />}
                      onClick={() => {
                        dropdownCloseOut(!dropdownOpen);
                        dispatch(updateIsFillerRemovalFTUX(false));
                      }}
                    >
                      {selectedFillerRemoval === NONE
                        ? 'Remove'
                        : getOptionTitle(selectedFillerRemoval)}
                    </TextButton>
                  </div>
                }
                options={[textOption, textAudioOption, noneOption]}
                onOuterClick={() => dropdownCloseOut(false)}
              />
            </div>
          )}
          {!fillerWordsPresent && (
            <TextButton
              size={iconSize}
              iconPosition="right"
              onClick={() => setIsFillerWordsDismissed(true)}
            >
              Dismiss
            </TextButton>
          )}
        </Arrange>
        {fillerWordsPresent && (
          <div className={styles.defaultFillerButtonWrapper}>
            <TextButton
              isDisabled={isDefaultOptionSelected}
              size="small"
              onClick={onDefaultSettingButtonClick}
            >
              <Text
                size="small"
                color={isDefaultOptionSelected ? 'disabledContent' : 'primary'}
                fontWeight="medium"
              >
                {isDefaultOptionSelected
                  ? 'This is your default setting'
                  : 'Make this my default setting'}
              </Text>
            </TextButton>
          </div>
        )}
      </Arrange>
    </div>
  );
};

export default FillerWordRemoval;
